/**
 * Map focus — country, city, district, or road.
 * Saved in localStorage under the signed-in account so sign-out does not wipe it.
 * Empty focus draws the whole map. A saved boundary hides everything outside it.
 */

export const FOCUS_PREFIX = "croc_ops_map_focus_v1:";

export const ROAD_CLASSES = [
  { id: "motorway", label: "Motorway", needles: ["motorway", "trunk", "mot"] },
  { id: "primary", label: "Primary", needles: ["primary", "pri"] },
  { id: "secondary", label: "Secondary", needles: ["secondary", "sec"] },
  { id: "street", label: "Street", needles: ["tertiary", "minor", "service", "residential", "street"] },
  { id: "path", label: "Path", needles: ["path", "track", "pedestrian", "footway"] },
];

const LEVELS = new Set(["country", "city", "district", "road"]);
const MAX_RING = 1200;

export function focusStorageKey(username) {
  const name = String(username || "").trim().toLowerCase();
  return name ? FOCUS_PREFIX + name : "";
}

export function emptyFocus() {
  return { mode: "multi", places: [], roads: [], exclusive: false };
}

export function loadMapFocus(username) {
  const key = focusStorageKey(username);
  if (!key) return emptyFocus();
  try {
    const raw = localStorage.getItem(key);
    return normalizeFocus(raw ? JSON.parse(raw) : null);
  } catch {
    return emptyFocus();
  }
}

export function saveMapFocus(username, focus) {
  const key = focusStorageKey(username);
  if (!key) return emptyFocus();
  const next = normalizeFocus(focus);
  try {
    if (!next.places.length && !next.roads.length) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* storage full or blocked — the in-memory focus still applies this visit */
  }
  return next;
}

export function normalizeFocus(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const mode = src.mode === "single" ? "single" : "multi";
  const roads = [];
  const seenRoads = new Set();
  for (const id of Array.isArray(src.roads) ? src.roads : []) {
    const key = String(id || "").trim();
    if (!ROAD_CLASSES.some((row) => row.id === key) || seenRoads.has(key)) continue;
    seenRoads.add(key);
    roads.push(key);
  }
  const places = [];
  const seen = new Set();
  for (const row of Array.isArray(src.places) ? src.places : []) {
    const place = normalizePlace(row);
    if (!place || seen.has(place.id)) continue;
    seen.add(place.id);
    places.push(place);
  }
  const exclusive = places.length > 0 && src.exclusive !== false;
  return { mode, places, roads, exclusive };
}

/** Page shows only these boards. The saved places stay; this is display state. */
export function enterMapFocus(focus) {
  const next = normalizeFocus(focus);
  if (!next.places.length) return { ...next, exclusive: false };
  return { ...next, exclusive: true };
}

/** Leave the exclusive page. Places and roads stay stored. */
export function leaveMapFocus(focus) {
  return normalizeFocus({ ...normalizeFocus(focus), exclusive: false });
}

function normalizePlace(row) {
  if (!row || typeof row !== "object") return null;
  const level = String(row.level || "").trim();
  const id = String(row.id || "").trim();
  const name = String(row.name || "").trim();
  if (!LEVELS.has(level) || !id || !name) return null;
  const geometry = normalizeGeometry(row.geometry);
  const bbox = normalizeBbox(row.bbox) || bboxOfGeometry(geometry);
  if (!geometry || !bbox) return null;
  return { id, name, level, bbox, geometry };
}

function normalizeBbox(raw) {
  if (!Array.isArray(raw) || raw.length < 4) return null;
  const nums = raw.slice(0, 4).map(Number);
  if (nums.some((n) => !Number.isFinite(n))) return null;
  const [w, s, e, n] = nums;
  if (e <= w || n <= s) return null;
  return [w, s, e, n];
}

function normalizeGeometry(geometry) {
  if (!geometry || typeof geometry !== "object") return null;
  const type = geometry.type;
  const coordinates = geometry.coordinates;
  if (type === "Polygon") return cleanPolygon(coordinates);
  if (type === "MultiPolygon") {
    const polys = cleanPolygons(coordinates);
    return polys.length ? { type, coordinates: polys } : null;
  }
  if (type === "LineString") {
    const line = cleanLine(coordinates);
    return line ? { type, coordinates: line } : null;
  }
  if (type === "MultiLineString") {
    const lines = (Array.isArray(coordinates) ? coordinates : []).map(cleanLine).filter(Boolean);
    return lines.length ? { type, coordinates: lines } : null;
  }
  return null;
}

function cleanPolygon(rings) {
  const out = (Array.isArray(rings) ? rings : []).map((ring) => decimate(cleanLine(ring))).filter((ring) => ring && ring.length >= 4);
  return out.length ? { type: "Polygon", coordinates: out } : null;
}

function cleanPolygons(polygons) {
  const out = [];
  for (const rings of Array.isArray(polygons) ? polygons : []) {
    const poly = cleanPolygon(rings);
    if (poly) out.push(poly.coordinates);
  }
  return out;
}

function cleanLine(line) {
  const out = [];
  for (const pt of Array.isArray(line) ? line : []) {
    if (!Array.isArray(pt) || pt.length < 2) continue;
    const x = Number(pt[0]);
    const y = Number(pt[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (Math.abs(x) > 180 || Math.abs(y) > 90) continue;
    out.push([x, y]);
  }
  return out.length >= 2 ? out : null;
}

function decimate(line) {
  if (!line || line.length <= MAX_RING) return line;
  const step = Math.ceil(line.length / MAX_RING);
  const out = [];
  for (let i = 0; i < line.length - 1; i += step) out.push(line[i]);
  const first = out[0];
  if (first) out.push(first);
  return out.length >= 4 ? out : line.slice(0, MAX_RING);
}

export function levelOfHit(row) {
  const type = String((row && row.type) || "").toLowerCase();
  const addr = String((row && row.addresstype) || "").toLowerCase();
  const cat = String((row && (row.category || row.class)) || "").toLowerCase();
  /* Nominatim often labels a country or state as type=administrative and puts the real kind in addresstype. */
  const kind = (type === "administrative" || type === "boundary" || !type) ? (addr || type) : type;
  if (kind === "country" || type === "country" || addr === "country") return "country";
  if (kind === "city" || kind === "town" || kind === "municipality" || kind === "village") return "city";
  if (
    kind === "suburb" || kind === "borough" || kind === "city_district" || kind === "county"
    || kind === "state_district" || kind === "district" || kind === "quarter" || kind === "neighbourhood"
    || kind === "state" || addr === "state"
  ) return "district";
  if (cat === "highway") return "road";
  return "";
}

export function placeFromHit(row) {
  const level = levelOfHit(row);
  if (!level || !row) return null;
  const id = "nominatim:" + String(row.osm_type || "n") + ":" + String(row.osm_id || row.place_id || "");
  const name = String(row.display_name || row.name || "").split(",").slice(0, 2).join(",").trim();
  const bb = Array.isArray(row.boundingbox) ? row.boundingbox.map(Number) : [];
  const bbox = bb.length === 4 && bb.every(Number.isFinite)
    ? [bb[2], bb[0], bb[3], bb[1]]
    : null;
  const geo = row.geojson || row.polygon_geojson || null;
  const geometry = geo && geo.type ? geo : null;
  const place = normalizePlace({ id, name, level, bbox, geometry });
  if (place) return place;
  if (!bbox) return null;
  return normalizePlace({
    id,
    name,
    level,
    bbox,
    geometry: { type: "Polygon", coordinates: [rect(bbox)] },
  });
}

function rect(bbox) {
  const [w, s, e, n] = bbox;
  return [[w, s], [e, s], [e, n], [w, n], [w, s]];
}

export function addPlace(focus, place) {
  const next = normalizeFocus(focus);
  const item = normalizePlace(place);
  if (!item) return next;
  const areas = next.places.filter((p) => p.level !== "road");
  const roads = next.places.filter((p) => p.level === "road");
  if (item.level === "road") {
    const without = roads.filter((p) => p.id !== item.id);
    const list = without.length === roads.length ? [...roads, item] : without;
    return { ...next, places: [...areas, ...list] };
  }
  if (next.mode === "single") return { ...next, places: [item, ...roads] };
  const without = areas.filter((p) => p.id !== item.id);
  const list = without.length === areas.length ? [...areas, item] : without;
  return { ...next, places: [...list, ...roads] };
}

export function setFocusMode(focus, mode) {
  const next = normalizeFocus({ ...focus, mode: mode === "single" ? "single" : "multi" });
  if (next.mode !== "single") return next;
  const area = next.places.find((p) => p.level !== "road");
  const roads = next.places.filter((p) => p.level === "road");
  return { ...next, places: area ? [area, ...roads] : roads };
}

export function toggleRoadClass(focus, id) {
  const next = normalizeFocus(focus);
  const key = String(id || "").trim();
  const roads = next.roads.includes(key) ? next.roads.filter((x) => x !== key) : [...next.roads, key];
  return normalizeFocus({ ...next, roads });
}

export function focusPlaceKey(focus) {
  return normalizeFocus(focus).places.map((p) => p.id).join("|");
}

function areaPlaces(focus) {
  return normalizeFocus(focus).places.filter((p) => p.level !== "road");
}

export function pointInFocus(ll, focus) {
  const areas = areaPlaces(focus);
  if (!areas.length) return true;
  if (!ll || ll.length < 2) return false;
  const x = Number(ll[0]);
  const y = Number(ll[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return areas.some((place) => geometryContains(place.geometry, x, y));
}

export function ringInFocus(ring, focus) {
  const areas = areaPlaces(focus);
  if (!areas.length) return true;
  return (Array.isArray(ring) ? ring : []).some((pt) => pointInFocus(pt, focus));
}

function geometryContains(geometry, x, y) {
  if (!geometry) return false;
  if (geometry.type === "Polygon") return polygonContains(geometry.coordinates, x, y);
  if (geometry.type === "MultiPolygon") return geometry.coordinates.some((rings) => polygonContains(rings, x, y));
  return false;
}

function polygonContains(rings, x, y) {
  if (!rings || !rings.length || !ringContains(rings[0], x, y)) return false;
  for (let i = 1; i < rings.length; i += 1) {
    if (ringContains(rings[i], x, y)) return false;
  }
  return true;
}

function ringContains(ring, x, y) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const hit = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi);
    if (hit) inside = !inside;
  }
  return inside;
}

function bboxOfGeometry(geometry) {
  const pts = [];
  collect(geometry, pts);
  if (!pts.length) return null;
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

function collect(geometry, into) {
  if (!geometry) return;
  if (geometry.type === "Polygon") geometry.coordinates.forEach((ring) => ring.forEach((pt) => into.push(pt)));
  else if (geometry.type === "MultiPolygon") geometry.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach((pt) => into.push(pt))));
  else if (geometry.type === "LineString") geometry.coordinates.forEach((pt) => into.push(pt));
  else if (geometry.type === "MultiLineString") geometry.coordinates.forEach((line) => line.forEach((pt) => into.push(pt)));
}

export function focusBounds(focus) {
  const places = normalizeFocus(focus).places;
  if (!places.length) return null;
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const place of places) {
    const box = place.bbox;
    if (!box) continue;
    w = Math.min(w, box[0]);
    s = Math.min(s, box[1]);
    e = Math.max(e, box[2]);
    n = Math.max(n, box[3]);
  }
  if (!Number.isFinite(w)) return null;
  return [w, s, e, n];
}

export function focusCamera(focus) {
  const box = focusBounds(focus);
  if (!box) return null;
  const levels = new Set(normalizeFocus(focus).places.map((p) => p.level));
  const near = levels.has("district") || levels.has("city") || levels.has("road");
  return {
    bounds: [[box[0], box[1]], [box[2], box[3]]],
    pitch: near ? 54 : 0,
    maxZoom: levels.has("district") ? 16.4 : levels.has("city") || levels.has("road") ? 15.4 : 5.5,
  };
}

function holeRing(ring) {
  const area = ringArea(ring);
  const copy = ring.slice();
  if (area > 0) copy.reverse();
  return copy;
}

function ringArea(ring) {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return sum / 2;
}

export function focusCollection(focus) {
  const places = areaPlaces(focus);
  const features = [];
  const holes = [];
  for (const place of places) {
    const outers = outerRings(place.geometry);
    for (const ring of outers) {
      const edge = ring[0][0] === ring[ring.length - 1][0] ? ring : [...ring, ring[0]];
      holes.push(holeRing(edge));
      features.push({
        type: "Feature",
        properties: { kind: "edge", name: place.name },
        geometry: { type: "LineString", coordinates: edge },
      });
    }
  }
  if (holes.length) {
    const world = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]];
    features.unshift({
      type: "Feature",
      properties: { kind: "mask" },
      geometry: { type: "Polygon", coordinates: [world, ...holes] },
    });
  }
  for (const place of normalizeFocus(focus).places) {
    if (place.level !== "road") continue;
    features.push({
      type: "Feature",
      properties: { kind: "road", name: place.name },
      geometry: place.geometry,
    });
  }
  return { type: "FeatureCollection", features };
}

function outerRings(geometry) {
  if (!geometry) return [];
  if (geometry.type === "Polygon") return geometry.coordinates[0] ? [geometry.coordinates[0]] : [];
  if (geometry.type === "MultiPolygon") return geometry.coordinates.map((rings) => rings[0]).filter(Boolean);
  return [];
}

/** null = not a road layer. true/false = show or hide that layer. */
export function roadLayerOn(layerId, roads) {
  const id = String(layerId || "").toLowerCase();
  if (!/road|bridge|tunnel|ops-motorway|ops-primary|ops-secondary|ops-minor|ops-service|ops-path/.test(id)) return null;
  if (/fence|focus|ops-focus/.test(id)) return null;
  const selected = Array.isArray(roads) ? roads : [];
  if (!selected.length) return true;
  const needles = [];
  for (const key of selected) {
    const row = ROAD_CLASSES.find((item) => item.id === key);
    if (row) needles.push(...row.needles);
  }
  return needles.some((needle) => id.includes(needle));
}

export function applyMapFocus(map, focus) {
  if (!map || typeof map.getStyle !== "function" || !map.getStyle()) return;
  const data = focusCollection(focus);
  if (!map.getSource("ops-focus")) {
    map.addSource("ops-focus", { type: "geojson", data });
    const before = map.getLayer("fence-fill") ? "fence-fill" : undefined;
    const add = (layer) => {
      if (before) map.addLayer(layer, before);
      else map.addLayer(layer);
    };
    add({
      id: "ops-focus-mask",
      type: "fill",
      source: "ops-focus",
      filter: ["==", ["get", "kind"], "mask"],
      paint: { "fill-color": "#16130F", "fill-opacity": 1 },
    });
    add({
      id: "ops-focus-line",
      type: "line",
      source: "ops-focus",
      filter: ["==", ["get", "kind"], "edge"],
      paint: { "line-color": "#E4B15A", "line-width": 2.5 },
    });
    add({
      id: "ops-focus-road",
      type: "line",
      source: "ops-focus",
      filter: ["==", ["get", "kind"], "road"],
      paint: { "line-color": "#D7E3D4", "line-width": 3 },
    });
  } else {
    map.getSource("ops-focus").setData(data);
  }
  const selected = normalizeFocus(focus).roads;
  const layers = map.getStyle().layers || [];
  for (const layer of layers) {
    const on = roadLayerOn(layer.id, selected);
    if (on == null) continue;
    try {
      map.setLayoutProperty(layer.id, "visibility", on ? "visible" : "none");
    } catch {
      /* a layer with no visibility layout is left as the style drew it */
    }
  }
  if (map.getLayer("ops-esri-transport")) {
    try {
      map.setLayoutProperty("ops-esri-transport", "visibility", selected.length ? "none" : "visible");
    } catch {
      /* */
    }
  }
}

export function focusLabel(focus) {
  return normalizeFocus(focus).places.map((p) => p.name).join(" · ");
}
